import { useState } from 'react';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-modal';
import { getPurchasedParts } from '../helpers/api';
import {
  accessTokenAtom,
  locationLovAtom,
  userIdAtom,
  loggedInUserAtom,
} from '../state';
import { useRecoilValue } from 'recoil';
import { round } from '../helpers/misc';
import Logger from '../helpers/Logger';

const logger = new Logger();

const PurchasedPartsSearchModal = props => {
  const { isOpen, hide, onSubmit } = props;
  const [partNumberSearchValue, setPartNumberSearchValue] = useState('');
  const [partDescriptionSearchValue, setPartDescriptionSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [locationSelect, setLocationSelect] = useState('');
  const [partTypeSelect, setPartTypeSelect] = useState('');
  const accessToken = useRecoilValue(accessTokenAtom);
  const locationLov = useRecoilValue(locationLovAtom);
  const userId = useRecoilValue(userIdAtom);
  const loggedInUser = useRecoilValue(loggedInUserAtom);
  
  const searchParts = async () => {
    try {
      setIsSearching(true);
      const parts = await getPurchasedParts(accessToken, partNumberSearchValue, partDescriptionSearchValue, locationSelect, partTypeSelect);
      setSearchResults(parts);
      setHasSearched(true);
    }
    catch(e) {
      console.error('PurchasedPartsSearchModal.searchParts error ->', e);
      logger.log(accessToken, `PurchasedPartsSearchModal.searchParts error -> ${JSON.stringify(e)}`, userId, loggedInUser);
      setSearchResults([]);
      setHasSearched(true);
    }
    finally {
      setIsSearching(false);
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      style={styles.partsModal}
      ariaHideApp={false}
    >
      <div style={styles.partsModalHeaderRow}>
        <h4 style={{ margin: 0 }}>Search Purchased Parts</h4>
        <FontAwesomeIcon
          icon={faTimes}
          size="lg"
          onClick={() => {
            setPartNumberSearchValue('');
            setPartDescriptionSearchValue('');
            setLocationSelect('');
            setPartTypeSelect('');
            setSearchResults([]);
            setHasSearched(false);
            hide();
          }}
          style={styles.closeTimes}
        />
      </div>
      <div style={styles.partsModalHeaderRow}>
        <div style={styles.partsModalSearchGroup}>
          <Form.Select
            as="select"
            style={{ marginRight: 10 }}
            value={locationSelect}
            onChange={e => setLocationSelect(e.target.value)}
          >
            <option value="">Any Location</option>
            {locationLov.map(o => <option key={o.code} value={o.code}>{o.name}</option>)}
          </Form.Select>
          <Form.Control
            type="text"
            placeholder="Number"
            value={partNumberSearchValue}
            onChange={e => setPartNumberSearchValue(e.target.value)}
            style={{ marginRight: 10 }}
          />
          <Form.Control
            type="text"
            placeholder="Description"
            value={partDescriptionSearchValue}
            onChange={e => setPartDescriptionSearchValue(e.target.value)}
            style={{ marginRight: 10 }}
          />
          <Form.Select
            as="select"
            style={{ marginRight: 10 }}
            value={partTypeSelect}
            onChange={e => setPartTypeSelect(e.target.value)}
          >
            <option value="">Any Type</option>
            <option value="ASSEMBLED">ASSEMBLED</option>
            <option value="MOLDED">MOLDED</option>
            <option value="PASSTHROUGH">PASSTHROUGH</option>
            <option value="PURCHASED">PURCHASED</option>
          </Form.Select>
          <Button
            type="button"
            variant="primary"
            onClick={searchParts}
            disabled={isSearching ||
              (!partNumberSearchValue.trim().length && !partDescriptionSearchValue.trim().length && !locationSelect.trim().length && !partTypeSelect.trim().length)}
          >
              <span style={styles.partsModalSearchButton}>
                <FontAwesomeIcon icon={faSearch} style={{ marginRight: 4 }}/>
                Search
              </span>
          </Button>
        </div>
        {
          hasSearched ?
            <h5 style={styles.headingResultsText}>
              {searchResults.length === 100 ?
                'Found over 100 matching parts' :
                `Found ${searchResults.length} matching parts`}
            </h5> :
            null
        }
      </div>
      <div style={styles.partsModalContent}>
        {
          isSearching ?
            <div style={styles.spinnerContainer}>
              <Spinner animation="border"/>
              <p style={styles.spinnerText}>Searching...</p>
            </div> :
            searchResults.length ?
              <Table striped>
                <thead>
                <tr>
                  <th>Org</th>
                  <th>Number</th>
                  <th>Description</th>
                  <th>Cost</th>
                  <th>Unit</th>
                  <th>Type</th>
                  <th>Last PO Date</th>
                  <th>Last PO Price</th>
                  <th>Currency</th>
                  <th>USD PO Price</th>
                </tr>
                </thead>
                <tbody>
                {searchResults.map((row, index) => (
                  <tr
                    key={index}
                    style={styles.clickableRow}
                    onClick={() => {
                      onSubmit(row);
                      setPartNumberSearchValue('');
                      setPartDescriptionSearchValue('');
                      setLocationSelect('');
                      setPartTypeSelect('');
                      setSearchResults([]);
                      setHasSearched(false);
                      hide();
                    }}
                  >
                    <td>{row.orgCode}</td>
                    <td>{row.partNumber}</td>
                    <td>{row.partDescription}</td>
                    <td>{row.partCost != null ? round(row.partCost, 5) : 'N/A'}</td>
                    <td>{row.unit}</td>
                    <td>{row.partType}</td>
                    <td>{row.lastPoDate != null ? row.lastPoDate : 'N/A'}</td>
                    <td>{row.lastPoPrice != null ? round(row.lastPoPrice, 5) : 'N/A'}</td>
                    <td>{row.currency}</td>
                    <td>{row.usdPoPrice != null ? round(row.usdPoPrice, 5) : 'N/A'}</td>
                  </tr>
                ))}
                </tbody>
              </Table> :
              null
        }
      </div>
    </Modal>
  );
};

const styles = {
  partsModal: {
    content: {
      top: 25,
      bottom: 25,
      left: 25,
      right: 25,
      borderRadius: 5,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
    },
  },
  partsModalContent: {
    flex: 1,
    overflowY: 'auto',
  },
  partsModalHeaderRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 10,
    borderBottom: 'solid 1px #CCCCCC',
  },
  partsModalSearchGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  partsModalSearchButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  closeTimes: {
    cursor: 'pointer',
  },
  spinnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 30,
  },
  spinnerText: {
    margin: 0,
    marginTop: 10,
    fontSize: 20,
  },
  headingResultsText: {
    margin: 0,
    alignSelf: 'center',
  },
  clickableRow: {
    cursor: 'pointer',
  },
};

export default PurchasedPartsSearchModal;